import { t } from "../../i18n";
import {
  CheckCircle2,
  LoaderCircle,
  ShieldAlert,
  Sparkles,
} from "lucide-react";
import {
  CONTEXT_COMPACTION_MARKER,
  type MessageActivityEvent,
} from "@lumen/shared";
import { Markdown } from "../Markdown";

export function ContextCompactionCard({
  event,
  messageContent,
}: {
  event: Extract<MessageActivityEvent, { type: "compaction" }>;
  messageContent: string;
}) {
  const running = event.status === "started";
  const failed = event.status === "failed";
  const summary = (event.summary ?? messageContent)
    .replace(CONTEXT_COMPACTION_MARKER, "")
    .split("\n")
    .slice(2)
    .join("\n")
    .trim();
  return (
    <article className="w-full">
      <div
        className={`rounded-2xl border p-4 text-sm ${
          failed
            ? "border-red-300 bg-red-50 text-red-900 dark:border-red-400/40 dark:bg-red-400/10 dark:text-red-100"
            : running
              ? "border-amber-300 bg-amber-50 text-amber-950 dark:border-amber-400/40 dark:bg-amber-400/10 dark:text-amber-100"
              : "border-emerald-300 bg-emerald-50 text-emerald-950 dark:border-emerald-400/40 dark:bg-emerald-400/10 dark:text-emerald-100"
        }`}
      >
        <div className="flex items-start gap-3">
          <div className="mt-0.5 shrink-0">
            {running ? (
              <LoaderCircle className="animate-spin" size={17} />
            ) : failed ? (
              <ShieldAlert size={17} />
            ) : (
              <CheckCircle2 size={17} />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2 font-semibold">
              <Sparkles size={15} />
              <span>
                {running
                  ? t("Context compaction started")
                  : failed
                    ? t("Context compaction failed")
                    : t("Context compacted")}
              </span>
              {!running && !failed && (
                <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-800 dark:bg-emerald-400/15 dark:text-emerald-200">
                  {event.omittedMessages > 0
                    ? t("older messages summarized", {
                        count: event.omittedMessages,
                      })
                    : t("Current context condensed")}
                </span>
              )}
            </div>
            <p className="mt-1 text-xs opacity-80">
              {running
                ? t(
                    "The conversation is being condensed for the next response.",
                  )
                : failed
                  ? event.error ||
                    t("The conversation checkpoint was not completed.")
                  : t(
                      "This compacted context will be used for later messages until the next compaction.",
                    )}
            </p>
            {!running && !failed && summary && (
              <details className="mt-3 min-w-0 overflow-hidden rounded-xl border border-current/20 bg-white/60 dark:bg-white/5">
                <summary className="cursor-pointer px-3 py-2 text-xs font-semibold">
                  {t("View compaction summary")}
                </summary>
                <div className="compaction-summary min-w-0 border-t border-current/20 p-3 text-xs leading-5 opacity-90">
                  <Markdown>{summary}</Markdown>
                </div>
              </details>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}
